import React from 'react';
import { getMessageByKey } from 'Stores/LanguageStore';
import { GenericStaffCollection } from 'Views/Components/_HumanWritten/StaffTables/GenericStaffCollection';
import { SortableHeaderButton } from 'Views/Components/_HumanWritten/StaffTables/SortableHeaderButton';
import { ParkingReservationRender } from './ParkingReservationRender';
import {
	StaffParkingReservationsFilters,
	useStaffParkingReservationsStore,
} from './StaffParkingReservationsFilterStore';
import { ParkingReservationListDto } from 'Services/Api/_HumanWritten/ParkingService';

export enum ParkingReservationOrderByOption {
	HUMAN_READABLE_ID = 'HUMAN_READABLE_ID',
	CREATED_DATE = 'CREATED_DATE',
	CUSTOMER = 'CUSTOMER',
	LOCATION = 'LOCATION',
	REGISTRATION = 'REGISTRATION',
	START_DATE = 'START_DATE',
	END_DATE = 'END_DATE',
	STATUS = 'STATUS',
	TOTAL_PRICE = 'TOTAL_PRICE',
	TRANSACTION_ID = 'TRANSACTION_ID',
}

export interface StaffParkingReservationsCollectionProps {
	reservations: ParkingReservationListDto[] | null;
	totalReservationCount: number;
	perPage: number;
	filters: StaffParkingReservationsFilters;
	setFilters: (newFilters: StaffParkingReservationsFilters) => void;
	isLoading: boolean;
	lastUpdated: Date;
	refresh: () => void;
	selectedReservationIds: Set<string>;
	setSelectedReservationIds: (ids: Set<string>) => void;
	hideCustomerColumn?: boolean;
}

export function StaffParkingReservationsCollection({
	reservations,
	totalReservationCount,
	perPage,
	filters,
	setFilters,
	isLoading,
	lastUpdated,
	refresh,
	selectedReservationIds,
	setSelectedReservationIds,
	hideCustomerColumn,
}: StaffParkingReservationsCollectionProps) {
	const { visibleColumns } = useStaffParkingReservationsStore();
	const visibleKeys = new Set(
		visibleColumns
			.filter(c => c.visible)
			.filter(c => !(hideCustomerColumn && c.key === 'customer'))
			.map(c => c.key),
	);

	const makeSortHandler = (field: string) => (sortField: string, desc: boolean) =>
		setFilters({
			...filters,
			orderBy: sortField as string,
			orderByDescending: desc,
			pageNo: 0,
		});

	return (
		<GenericStaffCollection
			items={reservations}
			totalItemCount={totalReservationCount}
			perPage={perPage}
			currentPage={filters.pageNo}
			onPageChange={page => setFilters({ ...filters, pageNo: page })}
			isLoading={isLoading}
			selectedItemIds={selectedReservationIds}
			setSelectedItemIds={setSelectedReservationIds}
			lastUpdated={lastUpdated}
			className="staff__table"
			renderRow={(reservation, isChecked, toggle) => (
				<ParkingReservationRender
					key={reservation.id}
					reservation={reservation}
					isChecked={isChecked}
					onToggleCheckbox={toggle}
					refresh={refresh}
					visibleColumns={visibleKeys}
				/>
			)}
			columns={(
				[
					{
						key: 'humanReadableId',
						label: 'Reservation ID',
						headerComponent: (
							<SortableHeaderButton
								text="Reservation ID"
								orderByField={ParkingReservationOrderByOption.HUMAN_READABLE_ID}
								currentOrderBy={filters.orderBy}
								isDescending={filters.orderByDescending}
								onSort={makeSortHandler(ParkingReservationOrderByOption.HUMAN_READABLE_ID)}
							/>
						),
					},
					{
						key: 'createdDate',
						label: 'Created',
						headerComponent: (
							<SortableHeaderButton
								text="Created"
								orderByField={ParkingReservationOrderByOption.CREATED_DATE}
								currentOrderBy={filters.orderBy}
								isDescending={filters.orderByDescending}
								onSort={makeSortHandler(ParkingReservationOrderByOption.CREATED_DATE)}
							/>
						),
					},
					{
						key: 'customer',
						label: 'Customer',
						headerComponent: (
							<SortableHeaderButton
								text="Customer"
								orderByField={ParkingReservationOrderByOption.CUSTOMER}
								currentOrderBy={filters.orderBy}
								isDescending={filters.orderByDescending}
								onSort={makeSortHandler(ParkingReservationOrderByOption.CUSTOMER)}
							/>
						),
					},
					{
						key: 'location',
						label: 'Location',
						headerComponent: (
							<SortableHeaderButton
								text="Location"
								orderByField={ParkingReservationOrderByOption.LOCATION}
								currentOrderBy={filters.orderBy}
								isDescending={filters.orderByDescending}
								onSort={makeSortHandler(ParkingReservationOrderByOption.LOCATION)}
							/>
						),
					},
					{
						key: 'registration',
						label: getMessageByKey('vehicle-tab__rego__label', 'Rego') ?? 'Rego',
						headerComponent: (
							<SortableHeaderButton
								text={getMessageByKey('vehicle-tab__rego__label', 'Rego') ?? 'Rego'}
								orderByField={ParkingReservationOrderByOption.REGISTRATION}
								currentOrderBy={filters.orderBy}
								isDescending={filters.orderByDescending}
								onSort={makeSortHandler(ParkingReservationOrderByOption.REGISTRATION)}
							/>
						),
					},
					{
						key: 'startDate',
						label: 'Start Date',
						headerComponent: (
							<SortableHeaderButton
								text="Start Date"
								orderByField={ParkingReservationOrderByOption.START_DATE}
								currentOrderBy={filters.orderBy}
								isDescending={filters.orderByDescending}
								onSort={makeSortHandler(ParkingReservationOrderByOption.START_DATE)}
							/>
						),
					},
					{
						key: 'endDate',
						label: 'End Date',
						headerComponent: (
							<SortableHeaderButton
								text="End Date"
								orderByField={ParkingReservationOrderByOption.END_DATE}
								currentOrderBy={filters.orderBy}
								isDescending={filters.orderByDescending}
								onSort={makeSortHandler(ParkingReservationOrderByOption.END_DATE)}
							/>
						),
					},
					{
						key: 'totalPrice',
						label: 'Total',
						headerComponent: (
							<SortableHeaderButton
								text="Total"
								orderByField={ParkingReservationOrderByOption.TOTAL_PRICE}
								currentOrderBy={filters.orderBy}
								isDescending={filters.orderByDescending}
								onSort={makeSortHandler(ParkingReservationOrderByOption.TOTAL_PRICE)}
							/>
						),
					},
					{
						key: 'transactionId',
						label: 'Transaction ID',
						headerComponent: (
							<SortableHeaderButton
								text="Transaction ID"
								orderByField={ParkingReservationOrderByOption.TRANSACTION_ID}
								currentOrderBy={filters.orderBy}
								isDescending={filters.orderByDescending}
								onSort={makeSortHandler(ParkingReservationOrderByOption.TRANSACTION_ID)}
							/>
						),
					},
					{
						key: 'status',
						label: 'Status',
						headerComponent: (
							<SortableHeaderButton
								text="Status"
								orderByField={ParkingReservationOrderByOption.STATUS}
								currentOrderBy={filters.orderBy}
								isDescending={filters.orderByDescending}
								onSort={makeSortHandler(ParkingReservationOrderByOption.STATUS)}
							/>
						),
					},
				] as { key: string; label: string; headerComponent?: React.ReactNode }[]
			).filter(col => visibleKeys.has(col.key))}
			emptyMessage="No parking reservations found"
			loadingMessage="Loading parking reservations"
		/>
	);
}
